import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { SystemOrAccountRegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { RolesFilterOption } from '../models/role-filter-option';
import { RoleRegisterOptions } from '../models/role-register-options';
import { RoleRegisterListItem } from '../models/role-register-results.model';
import * as roleRegisterActions from './role-register.actions';

export const roleRegisterFeatureKey = 'roleRegister';
export interface RoleRegisterState
   extends SystemOrAccountRegisterState<RoleRegisterListItem, RoleRegisterOptions, OrderingOption> {
   filters: RolesFilterOption[];
   includeInactive: boolean;
}

const adapter: RegisterAdapter<RoleRegisterListItem, RoleRegisterOptions, OrderingOption> = createRegisterAdapter<
   RoleRegisterListItem,
   RoleRegisterOptions,
   OrderingOption
>({
   selectId: (roleRegisterItem) => {
      return roleRegisterItem.roleId ?? 0;
   },
});

const initialState: RoleRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   includeInactive: false,
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'name' }],
   isOnSystemPage: false,
};

export const roleRegisterReducer = adapter.createReducer(
   roleRegisterActions.roleRegisterAdapterActions,
   initialState,
   on(roleRegisterActions.roleRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(roleRegisterActions.roleRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         roles: action.items,
         totalCount: action.totalCount,
      };
   }),
   on(roleRegisterActions.roleRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(roleRegisterActions.roleRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(roleRegisterActions.roleRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(roleRegisterActions.roleRegisterAdapterActions.clearRegisterFilters, (state) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
         includeInactive: initialState.includeInactive,
         orderingOptions: initialState.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   }),
   on(roleRegisterActions.loadSystemRolesSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(roleRegisterActions.loadAccountRolesSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   })
);
export const roleRegisterSelector = createFeatureSelector<RoleRegisterState>(roleRegisterFeatureKey);
export const selectors = adapter.getSelectors(roleRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(roleRegisterSelector);
